import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../../Axiosinstance";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  Grid,
  CircularProgress,
  MenuItem,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { CLOUDINARY_UPLOAD_PRESET, baseURL } from "../../../common";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import IconButton from "@mui/material/IconButton";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

const NewCompanyForm = ({ fee, dataFrom }) => {
  const { batchid } = useParams();
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [registerId, setRegisterId] = useState("");
  const [formData, setFormData] = useState({
    clientCompanyName: "",
    uen: "",
    companyAddress: "",
    postalCode: "",
    billingAddress: "",
    emailId: "",
    batchId: batchid,
    companyPhoneNumbers: [""],
    companyEmails: [""],
    companyTelephoneNumbers: [""],
    contacts: [
      {
        contactPerson: "",
        contactDesignation: "",
        contactPhoneNumbers: [""],
        courseAmount: fee,
        promotionCode: "",
        contactEmails: [""],
        contactTelephoneNumbers: [""],
        students: [
          {
            sector: "",
            name: "",
            idType: "",
            nationality: "",
            nric: "",
            dob: "",
            emailId: [""],
            contactNumber: [""],
            registration_Form: "",
            supporting_Documents: "",
            ic: "",
          },
        ],
      },
    ],
  });

  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const [uenAvailable, setUenAvailable] = useState(true);
  const [uenMessage, setUenMessage] = useState("");

  useEffect(() => {
    if (showBillingAddress) {
      setFormData((prevData) => ({
        ...prevData,
        billingAddress: prevData.companyAddress,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        billingAddress: "",
      }));
    }
  }, [showBillingAddress]);

  const handleCheckboxChange = (event) => {
    setShowBillingAddress(event.target.checked);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleContactChange = (index, e) => {
    const { name, value } = e.target;
    const updatedContacts = [...formData.contacts];
    updatedContacts[index][name] = value;
    setFormData({ ...formData, contacts: updatedContacts });
  };

  const handleStudentChange = (contactIndex, studentIndex, e) => {
    const { name, value } = e.target;
    const updatedContacts = [...formData.contacts];
    updatedContacts[contactIndex].students[studentIndex][name] = value;
    setFormData({ ...formData, contacts: updatedContacts });
  };

  const addStudent = (contactIndex) => {
    const updatedContacts = [...formData.contacts];
    updatedContacts[contactIndex].students.push({
      sector: "",
      name: "",
      idType: "",
      nric: "",
      nationality: "",
      dob: "",
      emailId: [""],
      contactNumber: [""],
      registration_Form: "",
      supporting_Documents: "",
      ic: "",
    });
    setFormData({ ...formData, contacts: updatedContacts });
  };

  const [contactEmail, setContactEmail] = useState("");

  const removeStudent = (contactIndex, studentIndex) => {
    const updatedContacts = [...formData.contacts];
    updatedContacts[contactIndex].students.splice(studentIndex, 1);
    setFormData({ ...formData, contacts: updatedContacts });
  };

  const handleFileUpload = async (e, contactIndex, studentIndex, fieldName) => {
    try {
      const file = e.target.files[0];

      // Check if a file is selected
      if (!file) return;

      const formDataUpload = new FormData(); // Create a new FormData object for file upload
      formDataUpload.append("file", file);
      formDataUpload.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

      const response = await fetch(baseURL, {
        method: "POST",
        body: formDataUpload,
      });

      const data = await response.json();

      if (data.secure_url) {
        const uploadedFileUrl = data.secure_url;

        // Update the student's corresponding field with the uploaded file URL
        setFormData((prevState) => {
          const updatedContacts = [...prevState.contacts];
          updatedContacts[contactIndex].students[studentIndex][fieldName] =
            uploadedFileUrl;

          return { ...prevState, contacts: updatedContacts };
        });
      }
    } catch (err) {
      console.error("File upload error:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if UEN is available before submitting the form
    if (!uenAvailable) {
      alert(uenMessage); // Show the UEN message as an alert
      return;
    }

    const structuredData = {
      ...formData,
      contacts: formData.contacts.map((contact) => ({
        ...contact,
        students: contact.students.map((student) => ({
          ...student,
          dob: new Date(student.dob).toISOString(),
        })),
      })),
    };

    setLoading(true);
    console.error(structuredData);
    try {
      const response = await axios.post(
        "/wsq/companies/create",
        structuredData
      );
      if (response.status === 200) {
        setRegisterId(response.data.registerId); // Store the registerId from the response
        setOpenModal(true); // Open success modal
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const checkUENAvailability = async (uen) => {
    try {
      const response = await axios.get(`/wsq/companies/check-uen?uen=${uen}`);

      if (response.status === 200) {
        // UEN is taken
        setUenAvailable(false);
        setUenMessage("UEN is already taken. Please enter a different UEN.");
      } else if (response.status === 202) {
        // UEN is new, allow the submission
        setUenAvailable(true);
        setUenMessage("UEN is available for registration.");
      }
    } catch (error) {
      console.error("Error checking UEN availability:", error);
      setUenAvailable(false);
      setUenMessage("Error checking UEN availability.");
    }
  };

  // General function to handle changes in array fields
  const handleArrayChange = (fieldPath, index, value) => {
    const updatedData = { ...formData };
    const fieldArray = getNestedField(updatedData, fieldPath);
    if (fieldArray) {
      fieldArray[index] = value;
      setFormData(updatedData);
    }
  };

  // General function to add a new item to an array field
  const addArrayItem = (fieldPath, newValue = "") => {
    const updatedData = { ...formData };
    const fieldArray = getNestedField(updatedData, fieldPath);
    if (fieldArray) {
      fieldArray.push(newValue); // Add new item
      setFormData(updatedData);
    }
  };

  // General function to remove an item from an array field, ensuring at least one item remains
  const removeArrayItem = (fieldPath, index) => {
    const updatedData = { ...formData };
    const fieldArray = getNestedField(updatedData, fieldPath);
    if (fieldArray && fieldArray.length > 1) {
      fieldArray.splice(index, 1); // Remove item at specified index
      setFormData(updatedData);
    }
  };

  // Helper function to access nested fields based on path
  const getNestedField = (obj, path) => {
    return path.reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
      obj
    );
  };

  const validateUEN = (uen) => {
    // Check if UEN length is between 7 and 15
    return uen.length >= 7 && uen.length <= 15;
  };
  
  const handleUENBlur = () => {
    if (validateUEN(formData.uen)) {
      checkUENAvailability(formData.uen); // Proceed to check availability if valid
    } else {
      setUenAvailable(false); // Set availability to false
      setUenMessage("Please enter valid UEN"); // Display error message
    }
  

     
  };

 

  const handleCloseModal = () => {
    setOpenModal(false);
    // navigate('/HomeContainer'); // Navigate to HomeContainer after modal close
    if (dataFrom === "admin") {
      navigate("/wsq/student");
    } else {
      navigate("/HomeContainer"); // Replace '/AnotherPath' with the desired route
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        label="Client Company Name"
        variant="outlined"
        fullWidth
        margin="normal"
        required
        name="clientCompanyName"
        value={formData.clientCompanyName}
        onChange={handleChange}
      />
      <TextField
        label="UEN"
        variant="outlined"
        fullWidth
        margin="normal"
        required
        name="uen"
        value={formData.uen}
        onChange={handleChange}
        onBlur={handleUENBlur} // Call the check function on blur
        error={!uenAvailable} // Show error if UEN is not available
        helperText={uenMessage} // Show helper text for UEN validation
      />
      <TextField
        label="Company Address"
        variant="outlined"
        fullWidth
        margin="normal"
        required
        name="companyAddress"
        value={formData.companyAddress}
        onChange={handleChange}
      />
      <TextField
        label="Postal Code"
        variant="outlined"
        fullWidth
        margin="normal"
        required
        name="postalCode"
        value={formData.postalCode}
        onChange={handleChange}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={showBillingAddress}
            onChange={handleCheckboxChange}
            color="primary"
          />
        }
        label="Use Company Address as Billing Address"
      />
      <TextField
        label="Billing Address"
        variant="outlined"
        fullWidth
        margin="normal"
        multiline
        rows={4}
        name="billingAddress"
        value={formData.billingAddress}
        onChange={handleChange}
        disabled={showBillingAddress}
      />
      <TextField
        label="Email Id"
        variant="outlined"
        fullWidth
        margin="normal"
        required
        name="emailId"
        value={formData.emailId}
        onChange={handleChange}
      />

      {/* Company Phone Numbers Section */}
      <Typography variant="h6" gutterBottom>
  Company Phone Numbers
</Typography>
{formData.companyPhoneNumbers.map((phoneNumber, index) => (
  <Grid
    container
    alignItems="center"
    spacing={1}
    key={index}
    style={{ marginBottom: "10px" }}
  >
    {/* Phone Input */}
    <Grid item xs={10}>
      <TextField
        label="Phone Number"
        variant="outlined"
        value={phoneNumber}
        onChange={(e) => {
          const value = e.target.value;

          // Regular expression for validating phone number: starts with 6, 8, or 9, and has at most 8 digits
          const isValid = /^([689])\d{0,7}$/.test(value);

          if (isValid || value === "") {
            handleArrayChange(["companyPhoneNumbers"], index, value);
          }
        }}
        fullWidth
        InputProps={{
          style: {
            height: "56px", // Match Material-UI TextField height
            borderRadius: "4px",
          },
        }}
         placeholder="6/8/9XXXXXXX"
      />
    </Grid>

    {/* Add Button */}
    <Grid>
      <IconButton
        color="primary"
        onClick={() => addArrayItem(["companyPhoneNumbers"])}
      >
        <AddCircleOutlineIcon />
      </IconButton>
    </Grid>

    {/* Remove Button */}
    <Grid>
      {formData.companyPhoneNumbers.length > 1 && (
        <IconButton
          color="error"
          onClick={() => removeArrayItem(["companyPhoneNumbers"], index)}
        >
          <RemoveCircleOutlineIcon />
        </IconButton>
      )}
    </Grid>
  </Grid>
))}


      {/* Company Emails */}
      <Typography variant="h6" gutterBottom>
        Company Emails
      </Typography>
      {formData.companyEmails.map((email, index) => (
        <Grid
          container
          alignItems="center"
          spacing={1}
          key={index}
          style={{ marginBottom: "10px" }}
        >
          {/* Email Input */}
          <Grid item xs={10}>
            <TextField
              label={`Company Email ${index + 1}`}
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) =>
                handleArrayChange(["companyEmails"], index, e.target.value)
              }
            />
          </Grid>

          {/* Add Button */}
          <Grid>
            <IconButton
              color="primary"
              onClick={() => addArrayItem(["companyEmails"])}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Grid>

          {/* Remove Button */}
          <Grid>
          {formData.companyEmails.length > 1 && (
              <IconButton
                color="error"
                onClick={() => removeArrayItem(["companyEmails"], index)}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}

      {/* Company Telephone Numbers */}
      <Typography variant="h6" gutterBottom>
        Company Telephone Numbers
      </Typography>
      {formData.companyTelephoneNumbers.map((telephoneNumber, index) => (
        <Grid
          container
          alignItems="center"
          spacing={1}
          key={index}
          style={{ marginBottom: "10px" }}
        >
          {/* Telephone Number Input */}
          <Grid item xs={10}>
            <TextField
              label={`Company Telephone Number ${index + 1}`}
              variant="outlined"
              fullWidth
              value={telephoneNumber}
              onChange={(e) => {
                const value = e.target.value;
      
                // Validate input: starts with '6' and has at most 8 digits
                if (/^6\d{0,7}$/.test(value) || value === "") {
                  handleArrayChange(
                    ["companyTelephoneNumbers"],
                    index,
                    value
                  );
                }
              }}
              onInput={(e) => {
                // Limit input to 8 digits
                if (e.target.value.length > 8) {
                  e.target.value = e.target.value.slice(0, 8);
                }
              }}
              placeholder="6XXXXXXX"
            />
          </Grid>

          {/* Add Button */}
          <Grid>
            <IconButton
              color="primary"
              onClick={() => addArrayItem(["companyTelephoneNumbers"])}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Grid>

          {/* Remove Button */}
          <Grid>
          {formData.companyTelephoneNumbers.length > 1 && (
              <IconButton
                color="error"
                onClick={() =>
                  removeArrayItem(["companyTelephoneNumbers"], index)
                }
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}

      <br />

      <Typography variant="h5" gutterBottom textAlign={"center"}>
        Contact Details
      </Typography>
      <Divider />

      {formData.contacts.map((contact, contactIndex) => (
        <div key={contactIndex}>
          <TextField
            label="Contact Person"
            variant="outlined"
            fullWidth
            margin="normal"
            // required
            name="contactPerson"
            value={contact.contactPerson}
            onChange={(e) => handleContactChange(contactIndex, e)}
          />

          <TextField
            label="Contact Email ID"
            variant="outlined"
            fullWidth
            margin="normal"
            // required
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />

          <TextField
            label="Designation"
            variant="outlined"
            fullWidth
            margin="normal"
            // required
            name="contactDesignation"
            value={contact.contactDesignation} // Bind to formData
            onChange={(e) => handleContactChange(contactIndex, e)}
          />

          {/* Dynamic Phone Numbers */}
          <Typography variant="h6" gutterBottom>
            Contact Phone Numbers
          </Typography>
          {contact.contactPhoneNumbers.map((phoneNumber, phoneIndex) => (
            <Grid
              container
              alignItems="center"
              spacing={1}
              key={phoneIndex}
              style={{ marginBottom: "10px" }}
            >
              {/* Phone Input */}
              <Grid item xs={10}>
                <PhoneInput
                  country={"sg"} // Set the default country
                  value={phoneNumber}
                  onChange={(value) =>
                    handleArrayChange(
                      ["contacts", contactIndex, "contactPhoneNumbers"],
                      phoneIndex,
                      value
                    )
                  }
                  inputStyle={{
                    width: "100%", // Full width styling
                    height: "56px", // Match Material-UI's TextField height
                    borderRadius: "4px", // Consistent border radius
                  }}
                />
              </Grid>

              {/* Add Button */}
              <Grid>
                <IconButton
                  color="primary"
                  onClick={() =>
                    addArrayItem([
                      "contacts",
                      contactIndex,
                      "contactPhoneNumbers",
                    ])
                  }
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Grid>

              {/* Remove Button */}
              <Grid>
                {phoneIndex > 0 && (
                  <IconButton
                    color="error"
                    onClick={() =>
                      removeArrayItem(
                        ["contacts", contactIndex, "contactPhoneNumbers"],
                        phoneIndex
                      )
                    }
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          {/* Contact Emails */}
          <Typography variant="h6" gutterBottom>
            Contact Emails
          </Typography>
          {contact.contactEmails.map((email, emailIndex) => (
            <Grid
              container
              alignItems="center"
              spacing={1}
              key={emailIndex}
              style={{ marginBottom: "10px" }}
            >
              {/* Email Input */}
              <Grid item xs={10}>
                <TextField
                  label={`Contact Email ${emailIndex + 1}`}
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={(e) =>
                    handleArrayChange(
                      ["contacts", contactIndex, "contactEmails"],
                      emailIndex,
                      e.target.value
                    )
                  }
                />
              </Grid>

              {/* Add Button */}
              <Grid>
                <IconButton
                  color="primary"
                  onClick={() =>
                    addArrayItem(["contacts", contactIndex, "contactEmails"])
                  }
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Grid>

              {/* Remove Button */}
              <Grid>
                {emailIndex > 0 && (
                  <IconButton
                    color="error"
                    onClick={() =>
                      removeArrayItem(
                        ["contacts", contactIndex, "contactEmails"],
                        emailIndex
                      )
                    }
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          {/* Contact Telephone Numbers */}
          <Typography variant="h6" gutterBottom>
            Contact Telephone Numbers
          </Typography>
          {contact.contactTelephoneNumbers.map((telephone, telephoneIndex) => (
            <Grid
              container
              alignItems="center"
              spacing={1}
              key={telephoneIndex}
              style={{ marginBottom: "10px" }}
            >
              {/* Telephone Input */}
              <Grid item xs={10}>
                <TextField
                  label={`Contact Telephone ${telephoneIndex + 1}`}
                  variant="outlined"
                  fullWidth
                  value={telephone}
                  onChange={(e) => {
                    const value = e.target.value;
          
                    // Validate input: starts with '6' and allows up to 8 digits
                    if (/^6\d{0,7}$/.test(value) || value === "") {
                      handleArrayChange(
                        ["contacts", contactIndex, "contactTelephoneNumbers"],
                        telephoneIndex,
                        value
                      );
                    }
                  }}
                  onInput={(e) => {
                    // Limit input to 8 digits
                    if (e.target.value.length > 8) {
                      e.target.value = e.target.value.slice(0, 8);
                    }
                  }}
                  required
                  placeholder="6XXXXXXX"  // Example format
                />
              </Grid>

              {/* Add Button */}
              <Grid>
                <IconButton
                  color="primary"
                  onClick={() =>
                    addArrayItem([
                      "contacts",
                      contactIndex,
                      "contactTelephoneNumbers",
                    ])
                  }
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Grid>

              {/* Remove Button */}
              <Grid>
                {telephoneIndex > 0 && (
                  <IconButton
                    color="error"
                    onClick={() =>
                      removeArrayItem(
                        ["contacts", contactIndex, "contactTelephoneNumbers"],
                        telephoneIndex
                      )
                    }
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          {contact.students.map((student, studentIndex) => (
            <>
              <Typography variant="h4">Trainee {studentIndex + 1}</Typography>
              <Divider />
              <br />
              <Grid container spacing={2} key={studentIndex}>
                <Grid item xs={6}>
                  <TextField
                    label="Student Name"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="name"
                    value={student.name}
                    onChange={(e) =>
                      handleStudentChange(contactIndex, studentIndex, e)
                    }
                  // required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="ID Type"
                    name="idType"
                    select
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    required
                    value={student.idType}
                    onChange={(e) =>
                      handleStudentChange(contactIndex, studentIndex, e)
                    }
                  >
                    <MenuItem value="NRIC">NRIC</MenuItem>
                    <MenuItem value="FIN">FIN</MenuItem>
                    <MenuItem value="Passport">Passport</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={student.idType || "ID"}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="nric"
                    value={student.nric}
                    onChange={(e) =>
                      handleStudentChange(contactIndex, studentIndex, e)
                    }
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Nationality"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="nationality"
                    value={student.nationality}
                    onChange={(e) =>
                      handleStudentChange(contactIndex, studentIndex, e)
                    }
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Sector"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="sector"
                    value={student.sector}
                    onChange={(e) =>
                      handleStudentChange(contactIndex, studentIndex, e)
                    }
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Date of Birth"
                    
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    type="date"
                    name="dob"
                    value={student.dob}
                    onChange={(e) =>
                      handleStudentChange(contactIndex, studentIndex, e)
                    }
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                {/* //email */}
                <Grid item xs={12}>
                  {student.emailId.map((email, emailIndex) => (
                    <Grid container spacing={2} key={emailIndex} alignItems="center">
                      <Grid item xs={9}>
                        <TextField
                          label={`Email ID ${emailIndex + 1}`}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          name="emailId"
                          value={email}
                          onChange={(e) =>
                            handleArrayChange(
                              ["contacts", contactIndex, "students", studentIndex, "emailId"],
                              emailIndex,
                              e.target.value
                            )
                          }
                        
                        />
                      </Grid>

                      <Grid item xs={3} container justifyContent="flex-start" spacing={1} alignItems="center">
                       
                        <Grid item>
                          <IconButton
                            color="secondary"
                            onClick={() =>
                              removeArrayItem(
                                ["contacts", contactIndex, "students", studentIndex, "emailId"],
                                emailIndex
                              )
                            }
                            disabled={student.emailId.length === 1}
                          >
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>

                  ))}

<Grid item>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() =>
                              addArrayItem(
                                ["contacts", contactIndex, "students", studentIndex, "emailId"],
                                ""
                              )
                            }
                          >
                             Add Email
                          </Button>
                        </Grid>
                  {/* Dynamic Contact Numbers */}
                  {student.contactNumber.map((number, numberIndex) => (
                    <Grid container spacing={2} key={numberIndex}>
                      <Grid item xs={9}>
                        <TextField
                          label={`Contact Number ${numberIndex + 1}`}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          name="contactNumber"
                          value={number}
                          onChange={(e) => {
                            const value = e.target.value;
                  
                            // Validate input: starts with '6' and has at most 8 digits
                            if (/^[689]\d{0,7}$/.test(value) || value === "") {
                              handleArrayChange(
                                [
                                  "contacts",
                                  contactIndex,
                                  "students",
                                  studentIndex,
                                  "contactNumber",
                                ],
                                numberIndex,
                                value
                              );
                            }
                          }}
                          onInput={(e) => {
                            // Limit input to 8 digits
                            if (e.target.value.length > 8) {
                              e.target.value = e.target.value.slice(0, 8);
                            }
                          }}
                         
                          placeholder="6/8/9/XXXXXX" 
                        />
                      </Grid>
                      <Grid item xs={3} container justifyContent="flex-start" spacing={1} alignItems="center">
                       
                        <Grid item>
                          <IconButton
                          
                            color="primary"
                            onClick={() =>
                              removeArrayItem(
                                [
                                  "contacts",
                                  contactIndex,
                                  "students",
                                  studentIndex,
                                  "contactNumber",
                                ],
                                numberIndex
                              )
                            }
                            disabled={student.contactNumber.length === 1}
                          >
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        </Grid>

                      </Grid>
                    </Grid>

                  ))}

                   <Grid item>
                          <Button
                          variant="outlined"
                            color="secondary"
                            onClick={() =>
                              addArrayItem(
                                [
                                  "contacts",
                                  contactIndex,
                                  "students",
                                  studentIndex,
                                  "contactNumber",
                                ],
                                ""
                              )
                            }
                          >
                           Add Contact Number

                          </Button>
                        </Grid>

                </Grid>

                {/* New Fields for Registration Form, Supporting Documents, and IC */}
                <Grid item xs={6}>
                  <TextField
                    label="IC"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="ic"
                    type="file"
                    onChange={(e) =>
                      handleFileUpload(e, contactIndex, studentIndex, "ic")
                    } // Remove value prop
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Registration Form"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="registration_Form"
                    type="file"
                    onChange={(e) =>
                      handleFileUpload(
                        e,
                        contactIndex,
                        studentIndex,
                        "registration_Form"
                      )
                    } // Remove value prop
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Supporting Documents"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="supporting_Documents"
                    type="file"
                    onChange={(e) =>
                      handleFileUpload(
                        e,
                        contactIndex,
                        studentIndex,
                        "supporting_Documents"
                      )
                    } // Remove value prop
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={6}>
                  {contact.students.length > 1 && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => removeStudent(contactIndex, studentIndex)}
                    >
                      Remove Trainee
                    </Button>
                  )}
                </Grid>
              </Grid>
            </>
          ))}

          {/* Add Student Button (display only after the last student) */}
          <Grid container justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={() => addStudent(contactIndex)}
            >
              Add Trainee
            </Button>
          </Grid>
        </div>
      ))}

      {/* Display Course Amount */}
      <Typography variant="h6" gutterBottom>
        Course Amount: SGD ${fee} (EXCL. GST)
      </Typography>
      <TextField
        label="Promotion Code"
        variant="outlined"
        fullWidth
        margin="normal"
        name="promotionCode"
        value={formData.contacts[0].promotionCode} // Update to get the value from the first contact
        onChange={(e) => handleContactChange(0, e)} // Ensure it updates the first contact's promotionCode
      />

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}>
        <Button
          type="button"
          variant="contained"
          color="error"
          onClick={() => {
            if (dataFrom === "admin") {
              navigate("/wsq/student");
            } else {
              navigate("/HomeContainer"); // Replace '/AnotherPath' with the desired route
            }
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Submit"}
        </Button>
      </div>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "24px",
            color: "#4caf50",
          }}
        >
          Registration Successful
        </DialogTitle>
        <DialogContent>
          <Grid container justifyContent="center">
            <CheckCircleOutline
              style={{ fontSize: "80px", color: "#4caf50" }}
            />
          </Grid>
          <DialogContentText
            style={{
              textAlign: "center",
              fontSize: "18px",
              marginTop: "20px",
              color: "#555",
            }}
          >
            Your registration was successfully submitted!
          </DialogContentText>
          <DialogContentText
            style={{
              textAlign: "center",
              fontSize: "16px",
              marginTop: "10px",
              color: "#777",
            }}
          >
            We have received your information and will be in touch with further
            details shortly.
          </DialogContentText>
          {registerId && (
            <DialogContentText
              style={{
                textAlign: "center",
                fontSize: "16px",
                marginTop: "10px",
                color: "#555",
              }}
            >
              Your Registration ID is: <strong>{registerId}</strong>
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="center">
            <Button
              onClick={handleCloseModal}
              variant="contained"
              style={{
                backgroundColor: "#4caf50",
                color: "#fff",
                padding: "10px 30px",
                fontSize: "16px",
              }}
            >
              OK
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export default NewCompanyForm;
